// =====================================================
// S-I18N-W1a — the locale must be known BEFORE the first paint, otherwise
// the page renders Persian, flips to Arabic a frame later and every number
// on screen changes digit script mid-animation.
//
// `bootstrapLocale()` is deliberately synchronous: it only reads the cookie
// and `navigator.languages` (links 4-5 of ADR-A3) and writes `<html lang dir>`.
// Links 1-3 come from the database and arrive with GET /v1/auth/me; when that
// answer differs, LocaleProvider switches once, which is a real preference
// change and not a flash.
// =====================================================

import { DEFAULT_LOCALE, setLocale, type AppLocale } from "./locale-state";
import {
  directionOfLocale,
  localeMetadata,
  type Direction,
  type PlatformLocaleTag,
} from "./locale-registry";
import { qaPseudoLocaleMetadata } from "./qa-pseudo";
import { readLocaleCookie, readNavigatorLanguages, pickFromAcceptLanguage } from "./resolve";
import bootRecoveryCopy from "virtual:i18n-boot-recovery";

/** A failed initial catalogue load must not mount an untranslated App. */
export const renderLocaleBootFailure = (
  container: HTMLElement,
  locale: AppLocale,
  reload: () => void = () => window.location.reload(),
): void => {
  const copy = bootRecoveryCopy[locale];
  const surface = document.createElement("section");
  surface.className = "shell";
  surface.style.paddingBlock = "48px";
  surface.setAttribute("role", "alert");
  const message = document.createElement("p");
  message.textContent = copy.errorMessage;
  const button = document.createElement("button");
  button.type = "button";
  button.className = "btn btn-outline";
  button.textContent = copy.retry;
  button.addEventListener("click", reload);
  surface.append(message, button);
  container.replaceChildren(surface);
};

interface DocumentLocaleAttributes {
  languageTag: string;
  direction: Direction;
  script?: string;
  fontStack?: string;
  qaPseudo?: boolean;
}

const applyDocumentLocaleAttributes = ({
  languageTag,
  direction,
  script,
  fontStack,
  qaPseudo = false,
}: DocumentLocaleAttributes): void => {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  el.setAttribute("lang", languageTag);
  el.setAttribute("dir", direction);
  el.dataset.locale = languageTag;
  el.dataset.direction = direction;
  if (script) el.dataset.script = script;
  else delete el.dataset.script;
  if (fontStack) el.dataset.fontStack = fontStack;
  else delete el.dataset.fontStack;
  if (qaPseudo) el.dataset.qaPseudoLocale = "true";
  else delete el.dataset.qaPseudoLocale;
  // Portals mount directly below body. Stamping it as well makes their
  // inherited direction deterministic even in embedded/test documents that
  // put a direction override between <html> and <body>.
  if (document.body) {
    document.body.setAttribute("dir", direction);
    document.body.dataset.locale = languageTag;
    document.body.dataset.direction = direction;
    if (qaPseudo) document.body.dataset.qaPseudoLocale = "true";
    else delete document.body.dataset.qaPseudoLocale;
  }
};

/** Write `<html lang dir>` so the browser's own bidi + font selection agree with us. */
export const applyDocumentLocale = (locale: AppLocale | PlatformLocaleTag): void => {
  const metadata = localeMetadata(locale);
  applyDocumentLocaleAttributes({
    languageTag: locale,
    direction: directionOfLocale(locale),
    script: metadata?.script,
    fontStack: metadata?.fontStack,
  });
};

/**
 * Stamp a registered pseudo locale for an explicitly QA-enabled browser build.
 * It never changes i18next resources, AppLocale, packs, or selector exposure.
 */
export const applyQaPseudoDocumentLocale = (raw: unknown): boolean => {
  const pseudo = qaPseudoLocaleMetadata(raw);
  if (!pseudo) return false;
  const reference = localeMetadata(pseudo.kind === "rtl" ? "ar" : "en");
  applyDocumentLocaleAttributes({
    languageTag: pseudo.languageTag,
    direction: pseudo.direction,
    script: reference?.script,
    fontStack: reference?.fontStack,
    qaPseudo: true,
  });
  return true;
};

/** Resolve links 4-6, publish them to the format/date layer, stamp the document. */
export const bootstrapLocale = (): AppLocale => {
  const resolved = readLocaleCookie() ?? pickFromAcceptLanguage(readNavigatorLanguages()) ?? DEFAULT_LOCALE;
  setLocale(resolved);
  applyDocumentLocale(resolved);
  return resolved;
};
